if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.statusRegion = this.querySelector('[data-product-form-status]');
        // The selection state (select / ready / sold-out / unavailable) and the
        // two transient flags are tracked apart: a cart request in flight keeps
        // the button guarded whatever the selection does in the meantime, and
        // the latest selection state is what shows once the request settles.
        this.submitState = this.submitButton.dataset.submitState || 'select';
        this.resolving = false;
        this.submitting = false;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          this.explainInactiveSubmit();
          return;
        }

        this.handleErrorMessage();
        this.setSubmitState('submitting');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              // A terminal state, so the busy flag is lifted with it below.
              this.setSubmitState('sold-out', { announce: false });
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.finishSubmitting();
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      // The one entry point for the Add to Cart button. Selection states:
      //   select       an option group is unanswered
      //   ready        the selected variant can be added
      //   sold-out     the selected variant has no stock
      //   unavailable  the selected combination does not exist
      // Transient states:
      //   pending      a selection is being resolved
      //   submitting   the cart request is in flight
      // A selection state sets the label and the data-submit-state hook the
      // stylesheet keys off, and ends any pending resolution. A transient
      // state only raises its flag; finishSubmitting() lowers the submitting
      // one when the cart request settles. applySubmitState() derives
      // aria-disabled and aria-busy from all three, so the button stays
      // guarded while a request is in flight even if the selection changes
      // underneath it. aria-disabled (never the disabled attribute) keeps the
      // button in the tab order so it can explain itself when activated, and
      // every selection change is read out through the status region.
      setSubmitState(state, { announce = true } = {}) {
        if (state === 'pending') {
          this.resolving = true;
          this.applySubmitState();
          return;
        }
        if (state === 'submitting') {
          this.submitting = true;
          this.applySubmitState();
          return;
        }

        const changed = state !== this.submitState;
        this.submitState = state;
        this.resolving = false;
        this.submitButton.dataset.submitState = state;
        this.submitButtonText.textContent = this.submitLabel(state);
        this.applySubmitState();
        if (announce && (changed || state !== 'select')) this.announce(() => this.statusMessage(state));
      }

      finishSubmitting() {
        this.submitting = false;
        this.applySubmitState();
      }

      applySubmitState() {
        const button = this.submitButton;
        const busy = this.resolving || this.submitting;

        if (busy) {
          button.setAttribute('aria-busy', 'true');
        } else {
          button.removeAttribute('aria-busy');
        }

        if (this.submitState === 'ready' && !busy) {
          button.removeAttribute('aria-disabled');
        } else {
          button.setAttribute('aria-disabled', 'true');
        }
      }

      submitLabel(state) {
        const strings = window.variantStrings;
        if (state === 'ready') return this.isGiftCard ? strings.addToCartSelf : strings.addToCart;
        if (state === 'sold-out') return strings.soldOut;
        if (state === 'unavailable') return strings.unavailable;
        return strings.selectOption;
      }

      statusMessage(state) {
        const strings = window.variantStrings;
        if (state === 'select') {
          const fieldset = this.firstIncompleteFieldset();
          return fieldset ? strings.statusOptionRequired.replace('[option]', this.fieldsetName(fieldset)) : '';
        }
        const template =
          state === 'ready' ? strings.statusReady : state === 'sold-out' ? strings.statusSoldOut : strings.statusUnavailable;
        return template.replace('[selection]', this.selectionSummary());
      }

      // Debounced so a burst of state changes (pending, ready, then select
      // when a color change clears the size) reads out once, as the final
      // state; the message is built when the timer fires so it reflects the
      // settled DOM.
      announce(buildMessage) {
        if (!this.statusRegion) return;
        clearTimeout(this.announceTimer);
        this.announceTimer = setTimeout(() => {
          const message = buildMessage();
          if (message) this.statusRegion.textContent = message;
        }, 150);
      }

      // Activation while aria-disabled. An unanswered option group gets the
      // inline alert and focus on its first radio, so the shopper lands on
      // the fix; sold out and unavailable repeat the label in the alert. Busy
      // states stay quiet: the outcome arrives through the status region.
      explainInactiveSubmit() {
        if (this.submitButton.getAttribute('aria-busy') === 'true') return;

        const strings = window.variantStrings;
        if (this.submitState === 'sold-out') return this.handleErrorMessage(strings.soldOut);
        if (this.submitState === 'unavailable') return this.handleErrorMessage(strings.unavailable);

        const fieldset = this.firstIncompleteFieldset();
        this.handleErrorMessage(
          fieldset ? strings.optionRequiredError.replace('[option]', this.fieldsetName(fieldset)) : strings.selectOption
        );

        const radio = fieldset?.querySelector('input[type="radio"]');
        if (!radio) return;
        radio.classList.add('focused');
        radio.addEventListener('blur', () => radio.classList.remove('focused'), { once: true });
        radio.focus();
      }

      get isGiftCard() {
        return this.submitButton.closest('.product-form__button-wrapper--gift-card') !== null;
      }

      // Option groups of the product this form belongs to (the PDP and each
      // quick-add modal have their own product-info). A re-rendered picker
      // keeps the outgoing <variant-selects> around, hidden, for a moment,
      // so only the visible one is read.
      get optionFieldsets() {
        const scope = this.closest('product-info') || document;
        const pickers = Array.from(scope.querySelectorAll('variant-selects')).filter(
          (picker) => picker.style.display !== 'none'
        );
        return pickers
          .flatMap((picker) => Array.from(picker.querySelectorAll('fieldset')))
          .filter((fieldset) => fieldset.style.display !== 'none');
      }

      firstIncompleteFieldset() {
        return this.optionFieldsets.find((fieldset) => !fieldset.querySelector('input:checked')) || null;
      }

      // Legend text minus the live value the swatch picker appends
      // ("Color: White" reads as "Color").
      fieldsetName(fieldset) {
        const legend = fieldset.querySelector('legend');
        if (!legend) return '';
        const copy = legend.cloneNode(true);
        copy.querySelectorAll('[data-selected-value]').forEach((node) => node.remove());
        return copy.textContent.replace(/:/g, ' ').replace(/\s+/g, ' ').trim();
      }

      selectionSummary() {
        return this.optionFieldsets
          .map((fieldset) => {
            const checked = fieldset.querySelector('input:checked');
            return checked ? `${this.fieldsetName(fieldset)} ${checked.value}`.trim() : null;
          })
          .filter(Boolean)
          .join(', ');
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
